/**
 * The practice trial-start and team-invite chain's analytics vocabulary: the
 * `action` tokens its two modals carry, the payload shapes their five events
 * report, and the emit seams the app binds.
 *
 * TWO SURFACES, ONE FILE, BECAUSE THEY SHARE A TOKEN. The practice trial modal's
 * primary CTA opens the invite modal, and its "Try on my own" secondary reports
 * `[PRACTICE INVITE] skip` — the invite modal's own token, not one of its own.
 * That is a real shared vocabulary, so the token is declared once here and both
 * surfaces' action unions name it. Declaring it twice is how one funnel step
 * becomes two.
 *
 * The bracketed, human-cased tokens (`[PRACTICE INVITE] skip`) are the oldest
 * spellings in this root and are FROZEN as-is. They read like labels rather than
 * keys, which is exactly why they must not be normalised: dashboards key on the
 * strings, and a tidy-up would be a new event.
 */
import type { TRIALS_AND_UPGRADES_SCREEN } from './trials-and-upgrades-analytics.types';

/** The `action` tokens the team-invite modal's three CTAs carry. */
export const PRACTICE_INVITE_ACTION = {
  sendInvites: '[PRACTICE INVITE] send invites',
  skip: '[PRACTICE INVITE] skip',
  close: '[PRACTICE INVITE] close',
} as const;

export type PracticeInviteActionToken =
  (typeof PRACTICE_INVITE_ACTION)[keyof typeof PRACTICE_INVITE_ACTION];

/** The `action` token the practice trial modal's primary CTA carries. */
export const PRACTICE_TRIAL_ACTION = {
  getStarted: '[PRACTICE TRIAL] get started',
} as const;

/**
 * What the practice trial modal's two CTAs report.
 *
 * The secondary reports the invite modal's `skip` rather than a token of its own
 * — see the file docblock. Spelled as a union over the two declarations so the
 * shared token has one home.
 */
export type PracticeTrialActionToken =
  | (typeof PRACTICE_TRIAL_ACTION)['getStarted']
  | (typeof PRACTICE_INVITE_ACTION)['skip'];

/** `modal_viewed` on the team-invite modal. Fired once per mount. */
export type PracticeInviteViewedProps = {
  screen_name: (typeof TRIALS_AND_UPGRADES_SCREEN)['practiceInviteTeam'];
};

/**
 * `button_clicked` on any of the invite modal's three CTAs.
 *
 * No `current_plan`: this modal is part of first-run onboarding, where the tier
 * has just been granted by the backend and the query has not necessarily
 * resolved. The events have never carried it and adding it here would change
 * what an existing row means.
 */
export type PracticeInviteClickProps = {
  screen_name: (typeof TRIALS_AND_UPGRADES_SCREEN)['practiceInviteTeam'];
  action: PracticeInviteActionToken;
  label: string;
};

/** `modal_viewed` on the practice trial-start modal. */
export type PracticeTrialViewedProps = {
  screen_name: (typeof TRIALS_AND_UPGRADES_SCREEN)['practiceTrial'];
};

/** `button_clicked` on the practice trial modal's primary or secondary CTA. */
export type PracticeTrialClickProps = {
  screen_name: (typeof TRIALS_AND_UPGRADES_SCREEN)['practiceTrial'];
  action: PracticeTrialActionToken;
  label: string;
};

/** The emit seam for the invite modal becoming visible. */
export type TrackPracticeInviteViewed = (props: PracticeInviteViewedProps) => void;

/** The emit seam for an invite-modal CTA. */
export type TrackPracticeInviteClick = (props: PracticeInviteClickProps) => void;

/** The emit seam for the practice trial modal becoming visible. */
export type TrackPracticeTrialViewed = (props: PracticeTrialViewedProps) => void;

/** The emit seam for a practice trial-modal CTA. */
export type TrackPracticeTrialClick = (props: PracticeTrialClickProps) => void;

/**
 * The invite modal's two seams as one app-supplied bundle.
 *
 * Split across the modal shell and its lazily-imported content today — the shell
 * owns the dismiss click and the content owns the view and the other two — so the
 * bundle is what makes the pair one seam rather than two half-seams.
 */
export type PracticeInviteAnalyticsPorts = {
  trackViewed: TrackPracticeInviteViewed;
  trackClick: TrackPracticeInviteClick;
};

/** The practice trial modal's two seams as one app-supplied bundle. */
export type PracticeTrialAnalyticsPorts = {
  trackViewed: TrackPracticeTrialViewed;
  trackClick: TrackPracticeTrialClick;
};
